/**
 * 各画面が使う API 呼び出し。パスの組み立てをここだけに閉じ込める。
 */

import { api } from './client.js';
import type {
  ApprovalDetailResponse,
  ApprovalHistoryResponse,
  ApprovalListResponse,
  ApprovalScopeResponse,
  CalculationTraceResponse,
  DailyResponse,
  DashboardResponse,
  Employee,
  GroupListResponse,
  MeResponse,
  MonthlyCalculateResponse,
  MonthlyReportsResponse,
  MonthlyStateResponse,
  PayrollMasterView,
  SaveResponse,
  StoredIssueView,
  SummaryResponse,
} from './types.js';

export interface DailyPayload {
  workType: string;
  directStart: number | null;
  officeIn: number | null;
  workStart: number | null;
  workEnd: number | null;
  officeOut: number | null;
  directHome: number | null;
  breakTotalMinutes: number | null;
  hourlyLeaveHours: number;
  breakOverrideMinutes: number | null;
  holidayWorkTreatment: string | null;
  siteName: string;
  taskNote: string;
  remarks: string;
}

const query = (params: Record<string, string | number | boolean | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
};

export const endpoints = {
  me: (): Promise<MeResponse> => api.get('/me'),

  // スタッフ
  monthlyReports: (month: string, employeeId?: string): Promise<MonthlyReportsResponse> =>
    api.get(`/reports${query({ month, employeeId })}`),
  daily: (date: string, employeeId?: string): Promise<DailyResponse> =>
    api.get(`/reports/${date}${query({ employeeId })}`),
  saveDaily: (
    date: string,
    payload: DailyPayload & { submit: boolean; revision?: number | null },
  ): Promise<SaveResponse> => api.put(`/reports/${date}`, payload),
  withdraw: (date: string): Promise<{ report: SaveResponse['report'] }> =>
    api.post(`/reports/${date}/withdraw`),
  bulkSave: (
    entries: (Partial<DailyPayload> & {
      date: string;
      submit?: boolean;
      revision?: number | null;
    })[],
  ): Promise<{ saved: number; days: SaveResponse[] }> => api.post('/reports/bulk', { entries }),
  summary: (month: string, employeeId?: string): Promise<SummaryResponse> =>
    api.get(`/summary${query({ month, employeeId })}`),

  // 上長
  approvals: (filter: 'all' | 'warning' | 'resubmitted'): Promise<ApprovalListResponse> =>
    api.get(`/approvals${query({ filter })}`),
  approvalDetail: (reportId: string): Promise<ApprovalDetailResponse> =>
    api.get(`/approvals/${reportId}`),
  decide: (
    reportId: string,
    decision: 'approve' | 'reject',
    comment: string,
  ): Promise<{ report: SaveResponse['report'] }> =>
    api.post(`/approvals/${reportId}/decision`, { decision, comment }),
  approvalHistory: (month?: string): Promise<ApprovalHistoryResponse> =>
    api.get(`/approvals/history${query({ month })}`),
  approvalScope: (): Promise<ApprovalScopeResponse> => api.get('/approvals/scope'),

  // 管理部
  dashboard: (month: string): Promise<DashboardResponse> =>
    api.get(`/admin/dashboard${query({ month })}`),
  issues: (
    month: string,
    resolved?: boolean,
  ): Promise<{ month: string; items: StoredIssueView[] }> =>
    api.get(`/admin/issues${query({ month, resolved })}`),
  resolveIssue: (issueId: string, note: string): Promise<{ issue: StoredIssueView }> =>
    api.post(`/admin/issues/${issueId}/resolve`, { note }),
  monthlyState: (month: string): Promise<MonthlyStateResponse> =>
    api.get(`/admin/monthly/${month}`),
  calculateMonth: (month: string): Promise<MonthlyCalculateResponse> =>
    api.post(`/admin/monthly/${month}/calculate`),
  monthlyDiff: (
    month: string,
  ): Promise<{
    month: string;
    upToDate: boolean;
    diffs: { employeeName: string; field: string; before: number; after: number }[];
  }> => api.get(`/admin/monthly/${month}/diff`),
  closeMonth: (month: string): Promise<{ month: string; snapshots: number; closedAt: string }> =>
    api.post(`/admin/monthly/${month}/close`),
  reopenMonth: (month: string, reason: string): Promise<unknown> =>
    api.post(`/admin/monthly/${month}/reopen`, { reason }),
  calculation: (employeeId: string, date: string): Promise<CalculationTraceResponse> =>
    api.get(`/admin/calculation/${employeeId}/${date}`),
  unapprove: (reportId: string, reason: string): Promise<unknown> =>
    api.post(`/admin/reports/${reportId}/unapprove`, { reason }),

  employees: (): Promise<{ items: Employee[] }> => api.get('/admin/employees'),
  createEmployee: (payload: Partial<Employee>): Promise<{ employee: Employee }> =>
    api.post('/admin/employees', payload),
  updateEmployee: (id: string, payload: Partial<Employee>): Promise<{ employee: Employee }> =>
    api.put(`/admin/employees/${id}`, payload),

  groups: (): Promise<GroupListResponse> => api.get('/admin/groups'),
  createGroup: (payload: {
    name: string;
    description: string;
    approverEmployeeId: number | null;
    memberEmployeeIds: number[];
  }): Promise<unknown> => api.post('/admin/groups', payload),
  updateGroup: (
    id: string,
    payload: {
      name: string;
      description: string;
      approverEmployeeId: number | null;
      memberEmployeeIds: number[];
    },
  ): Promise<unknown> => api.put(`/admin/groups/${id}`, payload),
  deleteGroup: (id: string): Promise<unknown> => api.delete(`/admin/groups/${id}`),

  payrollMasters: (): Promise<{ items: PayrollMasterView[] }> => api.get('/admin/payroll-masters'),
  updatePayrollMaster: (
    employeeId: string,
    payload: Omit<PayrollMasterView, 'employeeId' | 'employeeNumber' | 'name'>,
  ): Promise<unknown> => api.put(`/admin/payroll-masters/${employeeId}`, payload),
};
